"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Mail, MapPin, Phone } from "lucide-react";
import { SectionContainer } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TextGradient } from "@/components/ui/text-gradient";
import { cn, isValidEmail } from "@/lib/utils";

export function ContactSection() {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [formState, setFormState] = useState({
    name: "",
    email: "",
    message: "",
    submitted: false,
    errors: {
      name: false,
      email: false,
      message: false,
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors = {
      name: formState.name.trim() === "",
      email: !isValidEmail(formState.email),
      message: formState.message.trim() === ""
    };
    
    if (errors.name || errors.email || errors.message) {
      setFormState(prev => ({ ...prev, errors }));
      return;
    }
    
    // Form is valid, show success state
    setFormState(prev => ({ 
      ...prev, 
      submitted: true,
      errors: { name: false, email: false, message: false }
    }));
    
    // In a real app, you would send the form data to your server here
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value,
      errors: {
        ...prev.errors,
        [name]: false
      }
    }));
  };

  const contactInfo = [
    {
      icon: <Mail className="h-5 w-5" />,
      title: "Email Us",
      details: "info@bluelegosmatter.org",
      description: "We'll respond within 24 hours"
    },
    {
      icon: <Phone className="h-5 w-5" />,
      title: "Call Us",
      details: "(555) 123-4567",
      description: "Mon-Fri from 9am to 5pm"
    },
    {
      icon: <MapPin className="h-5 w-5" />,
      title: "Visit Us",
      details: "123 Blue Brick Avenue",
      description: "Legoland, CA 92056"
    }
  ];

  return (
    <SectionContainer className="bg-white">
      <SectionHeading
        title={
          <>
            Get in <TextGradient text="Touch" />
          </>
        }
        subtitle="Have questions about our initiatives or want to get involved? Reach out to us and we'll get back to you as soon as possible."
      />

      <div 
        ref={ref} 
        className="grid grid-cols-1 lg:grid-cols-3 gap-10"
      >
        <motion.div 
          className="lg:col-span-2"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8">
            {formState.submitted ? (
              <motion.div 
                className="text-center py-8" 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-blue-medium">
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-blue-medium mb-2">Message Sent!</h3>
                <p className="text-muted-foreground mb-6">
                  Thank you for reaching out. We'll get back to you soon.
                </p>
                <Button 
                  variant="blue-outline"
                  onClick={() => setFormState({
                    name: "",
                    email: "",
                    message: "",
                    submitted: false,
                    errors: { name: false, email: false, message: false }
                  })}
                >
                  Send Another Message
                </Button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      variant="premium"
                      error={formState.errors.name}
                      placeholder="Your name"
                      className="w-full"
                    />
                    {formState.errors.name && (
                      <p className="mt-1 text-sm text-red-500">Please enter your name</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formState.email}
                      onChange={handleChange}
                      variant="premium"
                      error={formState.errors.email}
                      placeholder="your@email.com"
                      className="w-full"
                    />
                    {formState.errors.email && (
                      <p className="mt-1 text-sm text-red-500">Please enter a valid email</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formState.message}
                      onChange={handleChange}
                      rows={5}
                      className={cn(
                        "w-full rounded-md border-2 border-blue-medium p-3 text-sm",
                        "focus:border-blue-dark focus:ring-blue-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                        formState.errors.message && "border-destructive focus-visible:ring-destructive"
                      )}
                      placeholder="How can we help you?"
                    />
                    {formState.errors.message && (
                      <p className="mt-1 text-sm text-red-500">Please enter your message</p>
                    )}
                  </div>
                  
                  <Button 
                    type="submit" 
                    variant="blue-solid" 
                    className="w-full md:w-auto" 
                    size="lg"
                    isAnimated
                  >
                    Send Message
                  </Button>
                </div>
              </form>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-6"
        >
          {contactInfo.map((info, i) => (
            <div 
              key={i}
              className={cn(
                "bg-white p-6 rounded-lg border border-gray-100 shadow-sm",
                "hover:border-blue-100 hover:shadow-md transition-all duration-300"
              )}
            >
              <div className="flex items-start">
                <div className="h-10 w-10 rounded-full bg-blue-light flex items-center justify-center text-blue-medium mr-4">
                  {info.icon}
                </div>
                <div>
                  <h3 className="font-medium mb-1">{info.title}</h3>
                  <p className="text-lg font-semibold mb-1">{info.details}</p>
                  <p className="text-sm text-muted-foreground">{info.description}</p>
                </div>
              </div>
            </div>
          ))}

          <div className="bg-gradient-to-r from-blue-700 to-blue-500 p-6 rounded-lg text-white">
            <h3 className="font-bold text-xl mb-3">Join Our Newsletter</h3>
            <p className="text-blue-50 mb-4">
              Get updates on our latest initiatives and ways to support blue Legos.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                placeholder="Your email"
                className="bg-white/90 text-foreground border-0"
              />
              <Button variant="secondary" className="whitespace-nowrap">
                Subscribe
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </SectionContainer>
  );
}